import { useState } from 'react';
import useFetch from '../../../hooks/useFetch';
import { buildEndpoint } from '../EndPoints';
import { ServiceEvents } from '../shared/interfaces';

export interface MembershipPlan {
    id: number;
    name: string;
    code: string;
    plan_type: string;
    plan_type_display: string;
    description: string;
    short_description: string;
    price: number;
    price_formatted: string;
    currency: string;
    duration_days: number;
    duration_display: string;
    contracts_included: number;
    contracts_display: string;
    chatbot_enabled: boolean;
    chatbot_messages_limit: number;
    features: string[];
    exclusions: string[];
    is_featured: boolean;
    display_order: number;
    badge_text: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

const useMembershipPlanService = () => {
    const { doGet, doPost } = useFetch();
    
    // Estados de carga
    const [fetchingGetPlans, setFetchingGetPlans] = useState(false);
    const [fetchingShowPlan, setFetchingShowPlan] = useState(false);
    const [fetchingStorePlan, setFetchingStorePlan] = useState(false);
    const [fetchingUpdatePlan, setFetchingUpdatePlan] = useState(false);
    const [fetchingDeletePlan, setFetchingDeletePlan] = useState(false);
    const [fetchingActivePlan, setFetchingActivePlan] = useState(false);

    const BASE_PATH = 'memberships/plans';

    /**
     * Obtiene todos los planes de membresía (admin)
     */
    const getMembershipPlans = (filters: any = {}, events: ServiceEvents = {}) => {
        const queryString = Object.entries(filters)
            .filter(([_, value]) => value !== undefined && value !== '')
            .map(([key, value]) => `${key}=${value}`)
            .join('&');
            
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}${queryString ? `?${queryString}` : ''}`),
            setFetching: setFetchingGetPlans
        });
    };

    /**
     * Obtiene el detalle de un plan
     */
    const showMembershipPlan = (id: number, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/${id}/show`),
            setFetching: setFetchingShowPlan
        });
    };

    /**
     * Crea un nuevo plan de membresía
     */
    const storeMembershipPlan = (data: any, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/store`),
            body: data,
            requestType: 'json',
            setFetching: setFetchingStorePlan
        });
    };

    /**
     * Actualiza un plan de membresía
     */
    const updateMembershipPlan = (id: number, data: any, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/${id}/update`),
            body: data,
            requestType: 'json',
            setFetching: setFetchingUpdatePlan
        });
    };

    /**
     * Elimina un plan de membresía
     */
    const deleteMembershipPlan = (id: number, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/${id}/delete`),
            setFetching: setFetchingDeletePlan
        });
    };

    /**
     * Activa o desactiva un plan de membresía
     */
    const activeMembershipPlan = (id: number, active: boolean, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/${id}/active`),
            body: { is_active: active },
            setFetching: setFetchingActivePlan
        });
    };

    // Estados para gestión de membresías de usuario
    const [fetchingUserMembership, setFetchingUserMembership] = useState(false);
    const [fetchingAssignMembership, setFetchingAssignMembership] = useState(false);
    const [fetchingRevokeMembership, setFetchingRevokeMembership] = useState(false);
    
    // Estados para cliente
    const [fetchingMyMembership, setFetchingMyMembership] = useState(false);
    const [fetchingCheckMembership, setFetchingCheckMembership] = useState(false);
    const [fetchingPurchase, setFetchingPurchase] = useState(false);
    const [fetchingPublicPlans, setFetchingPublicPlans] = useState(false);

    /**
     * Obtiene la membresía de un usuario específico (admin)
     */
    const getUserMembership = (userId: number, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`memberships/admin/user/${userId}`),
            setFetching: setFetchingUserMembership
        });
    };

    /**
     * Asigna una membresía a un usuario (admin)
     */
    const assignMembership = (data: { user_id: number; plan_id: number; duration_days?: number }, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint('memberships/admin/assign'),
            body: data,
            requestType: 'json',
            setFetching: setFetchingAssignMembership
        });
    };

    /**
     * Revoca la membresía de un usuario (admin)
     */
    const revokeMembership = (userId: number, reason?: string, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint('memberships/admin/revoke'),
            body: { user_id: userId, reason },
            requestType: 'json',
            setFetching: setFetchingRevokeMembership
        });
    };

    // =====================================
    // MÉTODOS PARA CLIENTE
    // =====================================

    /**
     * Obtiene planes públicos disponibles
     */
    const getPublicPlans = (events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint('memberships/plans/public'),
            setFetching: setFetchingPublicPlans
        });
    };

    /**
     * Obtiene mi membresía actual
     */
    const getMyMembership = (events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint('memberships/my-membership'),
            setFetching: setFetchingMyMembership
        });
    };

    /**
     * Verifica estado de membresía (resumen rápido)
     */
    const checkMembership = (events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint('memberships/check'),
            setFetching: setFetchingCheckMembership
        });
    };

    /**
     * Comprar/contratar un plan (pago único)
     */
    const purchasePlan = (planId: number, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint('memberships/purchase'),
            body: { plan_id: planId },
            requestType: 'json',
            setFetching: setFetchingPurchase
        });
    };

    /**
     * Crear suscripción recurrente con Flow
     */
    const createSubscription = (planId: number, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint('memberships/subscription/create'),
            body: { plan_id: planId },
            requestType: 'json',
            setFetching: setFetchingPurchase
        });
    };

    /**
     * Cancelar suscripción
     */
    const cancelSubscription = (atPeriodEnd = true, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint('memberships/subscription/cancel'),
            body: { at_period_end: atPeriodEnd },
            requestType: 'json',
            setFetching: setFetchingPurchase
        });
    };

    return {
        // Estados (admin)
        fetchingGetPlans,
        fetchingShowPlan,
        fetchingStorePlan,
        fetchingUpdatePlan,
        fetchingDeletePlan,
        fetchingActivePlan,
        fetchingUserMembership,
        fetchingAssignMembership,
        fetchingRevokeMembership,
        // Estados (cliente)
        fetchingMyMembership,
        fetchingCheckMembership,
        fetchingPurchase,
        fetchingPublicPlans,
        // Metodos (admin)
        getMembershipPlans,
        showMembershipPlan,
        storeMembershipPlan,
        updateMembershipPlan,
        deleteMembershipPlan,
        activeMembershipPlan,
        getUserMembership,
        assignMembership,
        revokeMembership,
        // Métodos (cliente)
        getPublicPlans,
        getMyMembership,
        checkMembership,
        purchasePlan,
        createSubscription,
        cancelSubscription
    };
};

export default useMembershipPlanService;
